import { useTranslation } from "react-i18next";
import { memo, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { track } from "../../../../common/util/tealium";
import defaultContent from "../../../../content/keyMetrics";
import LeafTile from "../../../../common/leaf/LeafTile/LeafTile";
import "./PerfIncomeKpiTile.scss";
import { KpiSelector } from "../../../../common/components/KpiSelector/KpiSelector";
import { IconGear } from "../../../../common/icons/IconGear/IconGear";
import TEALIUM from "../../../../common/enums/tealium-enums";
import { addParams } from "../../../../common/util/addParams";
import { Button } from "../../../../common/components/Button/Button";
import PerfIncomeKpiItemTile, { type PerfIncomeKpiItemProps } from "./PerIncomeKpiItemTile/PerfIncomeKpiItemTile";
import { FetchPerfIncomeKpiData } from "./PerfIncomeKpiTileDataLayer";
import { useUpdateDataviewKpisMutation } from "../../../../services/Dataview/DataviewAPI";
import { useAppSelector } from "../../../../store";
import { ErrorTile } from "../../../../common/components/ErrorTile/ErrorTile";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { trackCustomisedKpis } from "../../../../common/tealium/PerformanceIncomeDashboard/PerformanceIncomeDashboardTrack";

export const PerfIncomeKpiTile = memo(() => {
  const { t } = useTranslation(["keyMetrics"]);
  const [showKpiSelectorModal, setShowKpiSelectorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const gearButtonRef = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(false);
  const { minSelectionsAllowed, maxSelectionsAllowed, showPVBreakdown } = useAppSelector(
    (state) => state.boot.configuration.performanceIncomeKpis.keyMetrics,
  );

  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { availableKpis, selectedKpis, kpis, isLoading, isFetching, errorStatus, refetch, personalPVRequirementCode } =
    FetchPerfIncomeKpiData("landingPage", selectedPeriod);
  const [updateDataviewKpis] = useUpdateDataviewKpisMutation();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const kpiSelectorModalParam = queryParams.get("kpiSelectorModal") === "true";
  const [isMobile, setIsMobile] = useState(false);
  const getInitialLoadingTiles = () => {
    const stored = sessionStorage.getItem("perfIncomeKpiTileCount");
    return stored ? parseInt(stored, 10) : 4;
  };
  const [numLoadingTiles, setNumLoadingTiles] = useState(getInitialLoadingTiles);
  const updateNumLoadingTiles = (count: number) => {
    sessionStorage.setItem("perfIncomeKpiTileCount", String(count));
    setNumLoadingTiles(count);
  };

  useEffect(() => {
    if (!isLoading && !isFetching && !isSubmitting && !errorStatus) {
      if (numLoadingTiles !== selectedKpis.length) {
        updateNumLoadingTiles(selectedKpis.length);
      }
    }
  }, [isLoading, isFetching, isSubmitting, errorStatus, numLoadingTiles, selectedKpis.length]);

  useEffect(() => {
    if (kpiSelectorModalParam && !showKpiSelectorModal) {
      setShowKpiSelectorModal(false);
      addParams("kpiSelectorModal", "false", false, true);
    }
  }, [kpiSelectorModalParam, showKpiSelectorModal]);

  useEffect(() => {
    if (!!prevOpenRef.current && !showKpiSelectorModal && gearButtonRef && gearButtonRef.current) {
      gearButtonRef.current.focus();
    }
    prevOpenRef.current = showKpiSelectorModal;
  }, [showKpiSelectorModal]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");

    const handleChange = () => setIsMobile(media.matches);

    handleChange();
    media.addEventListener("change", handleChange);

    return () => media.removeEventListener("change", handleChange);
  }, []);

  const showSelectorModal = () => {
    trackCustomisedKpis(!showKpiSelectorModal);
    setShowKpiSelectorModal(!showKpiSelectorModal);
    addParams("kpiSelectorModal", (!showKpiSelectorModal).toString(), !showKpiSelectorModal, showKpiSelectorModal);
  };

  const submitSelectedKpis = async (newSelectedKpis: string[]) => {
    showSelectorModal();
    updateNumLoadingTiles(newSelectedKpis.length);
    setIsSubmitting(true);
    try {
      await updateDataviewKpis({
        dataviewId: "landingPage",
        selectedKpis: newSelectedKpis.join(","),
      });
      await refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save KPI preferences:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSortedKpis = () => {
    const sortedKpis = [...selectedKpis];
    [...availableKpis].forEach((kpi) => {
      if (!sortedKpis.includes(kpi)) {
        sortedKpis.push(kpi);
      }
    });
    return sortedKpis;
  };

  const getKpiItemData = (kpiKey: string): PerfIncomeKpiItemProps | null => {
    if (!kpis || kpis.length === 0) return null;
    return kpis.find((kpi) => kpi.label === kpiKey) || null;
  };

  const renderCustomizeButton = () => (
    <Button
      className="PerfIncomeKpiTile__customize-button"
      data-testid="perf-kpi-customize-button"
      onClick={showSelectorModal}
    >
      {`${t("customizeKpis", defaultContent.customizeKpis)}`}
      <IconGear />
    </Button>
  );

  const renderCustomizeTile = () => (
    <LeafTile hideBorder={false} isLoading={false} className="PerfIncomeKpiTile__customize-tile">
      <button
        className="PerfIncomeKpiTile__customize-tile-button"
        data-testid="perf-kpi-customize-button"
        onClick={showSelectorModal}
      >
        <span>{`${t("customizeKpis", defaultContent.customizeKpis)}`}</span>
        <IconGear />
      </button>
    </LeafTile>
  );

  const shouldRenderCustomizeAsTile = isMobile
    ? selectedKpis.length < 4 || (selectedKpis.length > 4 && selectedKpis.length % 2 !== 0)
    : selectedKpis.length > 4 && selectedKpis.length % 2 !== 0;

  if (errorStatus) {
    return (
      <div className="PerfIncomeKpiTile">
        <LeafTile hideBorder={true}>
          <ErrorTile
            clickFunction={refetch}
            errorStatus={errorStatus}
            isLoading={isLoading}
            tealiumTrackId="perf-income-kpi-tile"
          />
        </LeafTile>
      </div>
    );
  }

  const Loader = () => {
    const numTiles = numLoadingTiles;

    const renderCustomizeKpiAsTile = isMobile
      ? numLoadingTiles < 4 || (numLoadingTiles > 4 && numLoadingTiles % 2 !== 0)
      : numLoadingTiles > 4 && numLoadingTiles % 2 !== 0;

    return (
      <div className="PerfIncomeKpiTile__container">
        <div className={`PerfIncomeKpiTile__grid ${numTiles <= 4 ? "PerfIncomeKpiTile__grid--single-column" : ""}`}>
          {new Array(numTiles).fill(1).map((_, index) => (
            <LeafTile key={`PerfIncomeKpiItemTile--${index}`} className={`PerfIncomeKpiItemTile`}>
              <LeafRectangleLoader isLoading width="min(12rem, 80%)" height="1.1rem" margin="0" />
              <LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin=".5rem 0 0 0" />
            </LeafTile>
          ))}

          {renderCustomizeKpiAsTile && <LeafRectangleLoader height="100%" width="100%" isLoading margin="0" />}
        </div>
        {!renderCustomizeKpiAsTile && <LeafRectangleLoader width="100%" height="2.4rem" isLoading margin="0" />}
      </div>
    );
  };

  return (
    <div className="PerfIncomeKpiTile">
      {isLoading || isFetching || isSubmitting ? (
        <Loader />
      ) : (
        <LeafTile hideBorder={true}>
          {showKpiSelectorModal && (
            <div
              role="button"
              tabIndex={0}
              aria-label="Close KPI selector"
              onClick={showSelectorModal}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showSelectorModal();
                }
              }}
              className="PerfIncomeKpiTile__backdrop"
            />
          )}
          <div className="PerfIncomeKpiTile__container">
            <KpiSelector
              show={showKpiSelectorModal}
              availableKpis={getSortedKpis()}
              selectedKpis={selectedKpis}
              onCloseCallbackFn={showSelectorModal}
              minSelectionsAllowed={minSelectionsAllowed}
              maxSelectionsAllowed={maxSelectionsAllowed}
              onSubmitFn={submitSelectedKpis}
              personalPVRequirementCode={personalPVRequirementCode}
            />
            <div
              className={`PerfIncomeKpiTile__grid ${
                selectedKpis.length <= 4 ? "PerfIncomeKpiTile__grid--single-column" : ""
              }`}
            >
              {selectedKpis.map((kpiKey: string) => {
                const kpiItem = getKpiItemData(kpiKey);
                if (!kpiItem) return null;
                return <PerfIncomeKpiItemTile key={kpiKey} {...kpiItem} showPVBreakdown={showPVBreakdown} />;
              })}

              {shouldRenderCustomizeAsTile && renderCustomizeTile()}
            </div>
            {!shouldRenderCustomizeAsTile && renderCustomizeButton()}
          </div>
        </LeafTile>
      )}
    </div>
  );
});
